import { apiFetch, apiFetchJson } from "./apiClient";

// ─── Response types ────────────────────────────────────────────────────────

export interface CodexAccessEntry {
  characterId: number;
  characterName: string;
  userId: number;
  username: string;
}

export interface CodexSectionResponse {
  id: number;
  name: string;
  description: string;
  dmNotes?: string; // Private DM-only notes, never shared (owner only)
  sectionOrder: number;
  sharedWith: CodexAccessEntry[];
}

export interface CodexAsset {
  id: number;
  campaignId: number;
  name: string;
  description: string;
  dmNotes?: string; // Private DM-only notes, never shared (owner only)
  type: string;
  category: string;
  parentId?: number;
  sections: CodexSectionResponse[];
  sharedWith: CodexAccessEntry[];
  createdAt: string;
}

// ─── Service ──────────────────────────────────────────────────────────────

export const codexService = {
  /** Returns all campaign assets owned by the current user in the session's campaign. */
  async getMyAssets(sessionId: string): Promise<CodexAsset[]> {
    const res = await apiFetch(`/api/protected/sessions/${sessionId}/codex/assets`);
    if (!res.ok) throw new Error("Failed to load codex assets");
    const data = await res.json();
    return data ?? [];
  },

  /** Share an entire asset with one or more characters. */
  async shareAsset(
    sessionId: string,
    assetId: number,
    characterIds: number[]
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/sessions/${sessionId}/codex/assets/${assetId}/share`, {
      method: "POST",
      body: JSON.stringify({ characterIds }),
    });
  },

  /** Share a specific section with one or more characters. */
  async shareSection(
    sessionId: string,
    assetId: number,
    sectionId: number,
    characterIds: number[]
  ): Promise<{ message: string }> {
    return apiFetchJson(
      `/api/protected/sessions/${sessionId}/codex/assets/${assetId}/sections/${sectionId}/share`,
      {
        method: "POST",
        body: JSON.stringify({ characterIds }),
      }
    );
  },
};
